if (!customElements.get('loop-product-gallery')) {
  customElements.define('loop-product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
      this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
      this.stage = this.querySelector('.product-gallery__stage');
      this.slideScroller = this.querySelector('.product-gallery__slides');
      this.thumbnailScroller = this.querySelector('.product-gallery__thumbnails');
      this.desktopMedia = window.matchMedia('(min-width: 1281px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.scrollFrame = null;
      this.mobileScrollFrame = null;

      this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.show(this.index - 1), options);
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.show(this.index + 1), options);
      this.querySelector('[data-thumbnail-previous]')?.addEventListener('click', () => this.show(this.index - 1), options);
      this.querySelector('[data-thumbnail-next]')?.addEventListener('click', () => this.show(this.index + 1), options);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.show(Number(dot.dataset.galleryDot)), options);
      });

      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => this.show(Number(thumbnail.dataset.galleryThumbnail)), options);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
        if (event.key === 'ArrowRight') this.show(this.index + 1);
        if (this.desktopMedia.matches && event.key === 'ArrowUp') this.show(this.index - 1);
        if (this.desktopMedia.matches && event.key === 'ArrowDown') this.show(this.index + 1);
      }, options);

      this.slideScroller?.addEventListener('scroll', () => {
        if (this.desktopMedia.matches || this.mobileScrollFrame !== null) return;
        this.mobileScrollFrame = requestAnimationFrame(() => {
          this.mobileScrollFrame = null;
          const width = this.slideScroller.clientWidth;
          if (!width) return;
          const nextIndex = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.slideScroller.scrollLeft / width)));
          if (nextIndex !== this.index) {
            this.index = nextIndex;
            this.renderMobile();
          }
        });
      }, { ...options, passive: true });

      window.addEventListener('scroll', () => this.queueScrollSync(), { ...options, passive: true });
      window.addEventListener('resize', () => this.queueScrollSync(), { ...options, passive: true });
      this.desktopMedia.addEventListener('change', () => this.configureMode(), options);

      this.configureMode();
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.desktopMedia.matches) this.scrollMobile('instant');
      });
      if (this.slideScroller) this.resizeObserver.observe(this.slideScroller);
    }

    show(nextIndex) {
      if (!this.slides.length) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;

      if (this.desktopMedia.matches) {
        this.updateControls();
        this.slides[this.index].scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });
        return;
      }

      this.renderMobile();
      this.scrollMobile(this.reducedMotion.matches ? 'instant' : 'smooth');
    }

    scrollMobile(behavior) {
      this.slideScroller?.scrollTo({ left: this.index * this.slideScroller.clientWidth, behavior });
    }

    configureMode() {
      const desktop = this.desktopMedia.matches;
      this.stage?.setAttribute('aria-live', desktop ? 'off' : 'polite');

      if (desktop) {
        this.slides.forEach((slide) => {
          slide.hidden = false;
          slide.inert = false;
          slide.setAttribute('aria-hidden', 'false');
        });
        this.updateControls(false);
        this.queueScrollSync();
      } else {
        this.renderMobile();
        this.scrollMobile('instant');
      }
    }

    renderMobile() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.hidden = false;
        slide.inert = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelector('video')?.pause();
        if (Math.abs(index - this.index) <= 1) {
          const image = slide.querySelector('img');
          if (image) image.loading = 'eager';
        }
      });

      this.updateControls();
    }

    queueScrollSync() {
      if (!this.desktopMedia.matches || this.scrollFrame !== null) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const viewportCenter = window.innerHeight / 2;
        let nearestIndex = this.index;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.top + (rect.height / 2) - viewportCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        if (nearestIndex !== this.index) {
          this.index = nearestIndex;
          this.updateControls();
        }
      });
    }

    updateControls(scrollThumbnail = true) {
      this.slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === this.index);
      });

      [...this.dots, ...this.thumbnails].forEach((control) => {
        const controlIndex = Number(control.dataset.galleryDot ?? control.dataset.galleryThumbnail);
        if (controlIndex === this.index) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });

      const thumbnail = this.thumbnails[this.index];
      if (!scrollThumbnail || !thumbnail || !this.thumbnailScroller) return;
      const targetTop = thumbnail.offsetTop - ((this.thumbnailScroller.clientHeight - thumbnail.offsetHeight) / 2);
      this.thumbnailScroller.scrollTo({
        top: Math.max(0, targetTop),
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    disconnectedCallback() {
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      if (this.mobileScrollFrame !== null) cancelAnimationFrame(this.mobileScrollFrame);
      this.resizeObserver?.disconnect();
      this.controller?.abort();
    }
  });
}

if (!customElements.get('loop-product-form')) {
  customElements.define('loop-product-form', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.addButton = this.querySelector('[data-add-to-bag]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.currentPrice = document.querySelector('[data-product-current-price]');
      this.comparePrice = document.querySelector('[data-product-compare-price]');
      this.status = this.querySelector('[data-product-status]');
      this.fields = [...this.querySelectorAll('[data-product-option]')];
      this.adding = false;
      this.added = false;
      this.feedbackAnimations = [];
      this.bagLink = document.querySelector('.bag-link');

      try {
        this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }

      this.fields.forEach((field) => {
        field.addEventListener('change', () => this.updateVariant(true), options);
      });
      this.form?.addEventListener('submit', (event) => this.addToBag(event), options);
      this.updateVariant(false);
    }

    selectedOptions() {
      return this.fields.map((field) => field.querySelector('input:checked')?.value || '');
    }

    updateVariant(updateUrl) {
      const selected = this.selectedOptions();
      const variant = this.fields.length === 0
        ? this.variants[0]
        : this.variants.find((candidate) => (
          candidate.options.every((value, index) => value === selected[index])
        ));

      this.fields.forEach((field) => {
        const optionIndex = Number(field.dataset.optionIndex);
        field.querySelectorAll('input').forEach((input) => {
          const candidateSelection = [...selected];
          candidateSelection[optionIndex] = input.value;
          input.disabled = !this.variants.some((candidate) => (
            candidate.available && candidate.options.every((value, index) => value === candidateSelection[index])
          ));
        });
      });

      if (!variant) {
        this.variantInput.value = '';
        this.setAvailability(false);
        return;
      }

      this.variant = variant;
      this.variantInput.value = variant.id;
      this.currentPrice.textContent = variant.price;
      this.comparePrice.textContent = variant.compareAtPrice || '';
      this.comparePrice.hidden = !variant.compareAtPrice;
      this.setAvailability(variant.available);

      if (updateUrl && history.replaceState) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }

    setAvailability(available) {
      this.addButton.disabled = !available || this.adding || this.added;
      if (this.adding || this.added) return;
      this.addLabel.textContent = available
        ? this.addButton.dataset.availableLabel
        : this.addButton.dataset.soldOutLabel;
    }

    async addToBag(event) {
      if (!this.variant?.available || !window.fetch) return;
      event.preventDefault();
      if (this.adding || this.added) return;
      this.adding = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addLabel.textContent = this.dataset.addingLabel;
      this.fields.forEach((field) => { field.disabled = true; });
      this.status.textContent = '';
      this.status.classList.remove('is-success');

      try {
        const addUrl = new URL(this.form.action, location.origin);
        addUrl.pathname = `${addUrl.pathname.replace(/\/$/, '')}.js`;
        const { response, result } = await this.requestJson(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form),
        }, 15000);
        if (!response.ok) {
          const error = new Error(typeof result.description === 'string' ? result.description : this.dataset.errorLabel);
          error.cartRejected = true;
          throw error;
        }

        this.status.textContent = this.dataset.addedLabel;
        this.status.classList.add('is-success');
        this.showConfirmation();

        this.refreshBagCount();
      } catch (error) {
        if (this.controller.signal.aborted) return;
        this.status.classList.remove('is-success');
        this.status.textContent = error.cartRejected ? error.message : this.dataset.connectionErrorLabel;
      } finally {
        this.adding = false;
        this.fields.forEach((field) => { field.disabled = false; });
        this.addButton.removeAttribute('aria-busy');
        this.setAvailability(Boolean(this.variant?.available));
      }
    }

    async requestJson(url, options, timeout) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.controller.signal.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(abort, timeout);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const result = await response.json();
        return { response, result };
      } finally {
        clearTimeout(timer);
        this.controller.signal.removeEventListener('abort', abort);
      }
    }

    async refreshBagCount() {
      const root = window.Shopify?.routes?.root || '/';
      const refreshId = this.cartRefreshId = (this.cartRefreshId || 0) + 1;
      try {
        const { response, result: cart } = await this.requestJson(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }, 5000);
        if (!response.ok || refreshId !== this.cartRefreshId) return;
        document.querySelectorAll('.bag-count').forEach((count) => {
          count.textContent = cart.item_count;
          this.animateFeedback(count, [
            { transform: 'translateY(0) scale(1)' },
            { transform: 'translateY(-4px) scale(1.15)', offset: 0.45 },
            { transform: 'translateY(0) scale(1)' },
          ]);
        });
        const bagLink = document.querySelector('.bag-link');
        bagLink?.setAttribute('aria-label', `Bag, ${cart.item_count} items`);
      } catch (error) {
        // The item is already added; a badge refresh must not undo that confirmation.
      }
    }

    showConfirmation() {
      this.added = true;
      this.addButton.classList.add('is-added');
      this.addLabel.textContent = this.dataset.addedLabel;
      this.bagLink?.classList.add('is-confirmed');
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = setTimeout(() => {
        this.added = false;
        this.addButton.classList.remove('is-added');
        this.bagLink?.classList.remove('is-confirmed');
        this.setAvailability(Boolean(this.variant?.available));
      }, 1800);
    }

    animateFeedback(element, frames) {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.feedbackAnimations = this.feedbackAnimations.filter(animation => animation.playState === 'running');
      this.feedbackAnimations.push(element.animate(frames, {
        duration: 420,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      }));
    }

    disconnectedCallback() {
      clearTimeout(this.feedbackTimer);
      this.feedbackAnimations?.forEach(animation => animation.cancel());
      this.bagLink?.classList.remove('is-confirmed');
      this.controller?.abort();
    }
  });
}
